import {
  CountryChartRowItem,
  LanguageChartRowItem,
} from "@/models/frontend-only/ChartRowItem";
import {
  ChartBadge,
  FESearchResult,
} from "@/models/frontend-only/FESearchResult";
import { ChartCountryInfo } from "@/models/frontend-only/ChartCountryInfo";
import { defineStore } from "pinia";
import {
  AvailableChart,
  ChartLanguageInfo,
  ChartLanguageInfoWithType,
} from "@/models/frontend-only/ChartLanguageInfo";

export const useChartStore = defineStore("chart", () => {
  const beta = useBetaStore();
  const songStore = useSongStore();
  const profile = useProfileStore();
  const { t } = useI18n();

  const availableCountries = ref<ChartCountryInfo[]>([]);
  const availableChartsByLanguage = ref<AvailableChart[]>([]);
  const geoCharts = ref<{ [countryCode: string]: string[] }>({});
  const languageCharts = ref<{ [languageCode: string]: string[] }>({});
  const cachedChartDataByCountry = ref<{
    [countryCode: string]: { [weekOf: string]: FESearchResult[] };
  }>({});
  const cachedChartDataByLanguage = ref<{
    [languageCode: string]: { [monthOf: string]: FESearchResult[] };
  }>({});

  const availableChartsByCountry = computed(() => {
    const allCharts: AvailableChart[] = availableCountries.value.map((c) => {
      const urlChartType = c.ChartType || "ccli-top-100";
      return {
        urlChartType: urlChartType,
        chartType: "region",
        chartTypeLabel:
          c.ChartType === "ccli-top-100"
            ? t("SV.LIT_CCLITop100")
            : t("G.LIT_CCLITopSongs"),
        url: `/${c.ChartType}/${c.ChartCountryCode.toLowerCase()}`,
        limitTo: c.LimitRanksTo || 100,
        code: c.ChartCountryCode.toLowerCase(),
        codeLabel: c.ChartCountryName,
      };
    });
    const charts: typeof allCharts = [];
    allCharts.forEach((c) => {
      if (!charts.find((cc) => cc.code === c.code) && c.code !== null) {
        charts.push(c);
      }
    });
    return sortBy(charts, (c) => c.codeLabel);
  });

  const defaultChart = computed<AvailableChart>(() => {
    let country: ChartCountryInfo = {
      Country: "US",
      ChartCountryName: "United States",
      ServiceCenter: "United States",
      ChartCountryCode: "US",
      ChartType: "ccli-top-100",
      LimitRanksTo: 100,
      Visible: true,
      UseLanguageChart: false,
      LanguageChart: null,
    };

    const foundCountry = availableCountries.value.find(
      (c) => c.Country === profile.profile?.countryCode,
    );

    if (foundCountry) {
      country = foundCountry;
    }

    const languageEnabled = beta.features.ccliTop100ByLanguage;

    if (country.UseLanguageChart && country.LanguageChart && languageEnabled) {
      const languageFound = availableChartsByLanguage.value.find(
        (l) => l.code.toLowerCase() === country.LanguageChart!.toLowerCase(),
      ) || {
        chartType: "language",
        urlChartType: "ccli-top-100-by-language",
        chartTypeLabel: t("SV.LIT_CCLITop100"),
        url: `/ccli-top-100-by-language/en`,
        limitTo: 100,
        code: "en",
        codeLabel: "English",
      };

      return languageFound;
    } else {
      const regionFound = availableChartsByCountry.value.find(
        (c) => c.code.toLowerCase() === country.ChartCountryCode.toLowerCase(),
      ) || {
        chartType: "language",
        urlChartType: "ccli-top-100",
        chartTypeLabel: t("SV.LIT_CCLITop100"),
        url: `/ccli-top-100/us`,
        limitTo: 100,
        code: "us",
        codeLabel: "United States",
      };
      return regionFound;
    }
  });

  function isCCLILanguageChartAvailable(languageCode: string) {
    if (
      availableChartsByLanguage.value.findIndex(
        (c) => c.code === languageCode,
      ) === -1
    ) {
      return false;
    }
    return true;
  }

  function isCCLICountryChartAvailable(chartCountryCode: string) {
    if (
      availableChartsByCountry.value.findIndex(
        (c) => c.code.toLowerCase() === chartCountryCode.toLowerCase(),
      ) === -1
    ) {
      return false;
    }
    return true;
  }

  async function getChartMonthsByLanguage(languageCode: string) {
    if (!isCCLILanguageChartAvailable(languageCode)) {
      console.error("Invalid language code", languageCode);
      throw new Error("Invalid language code");
    }

    if (languageCharts.value[languageCode]) {
      return Array.from(languageCharts.value[languageCode]);
    }

    const data = await tokenFetch<{ chartMonth: string }[]>(
      `${AppConstants.CHART_BASE_LANGUAGE_URL}/${languageCode.toLowerCase()}/chartMonths.json`,
      true,
    );

    const dataMonths = data.map((d) => d.chartMonth);

    languageCharts.value[languageCode] = dataMonths;

    return Array.from(languageCharts.value[languageCode]);
  }

  async function getChartWeeksByCountry(chartCountryCode: string) {
    if (!isCCLICountryChartAvailable(chartCountryCode)) {
      console.error("Invalid country code", chartCountryCode);
      throw new Error("Invalid country code");
    }

    if (geoCharts.value[chartCountryCode]) {
      return Array.from(geoCharts.value[chartCountryCode]);
    }

    const data = await tokenFetch<{ WeekOf: string }[]>(
      `${AppConstants.CHART_BASE_COUNTRY_URL}/${chartCountryCode.toUpperCase()}/chartWeeks.json`,
      true,
    );

    const dataWeeks = data.map((d) => d.WeekOf);

    geoCharts.value[chartCountryCode] = dataWeeks;

    return Array.from(geoCharts.value[chartCountryCode]);
  }

  async function getMostRecentChartByCountry(chartCountryCode: string) {
    const chartWeeks = await getChartWeeksByCountry(chartCountryCode);
    const dates = sortBy(chartWeeks, (c) => c);
    let results: FESearchResult[] | null = null;
    let date: string = "";
    do {
      date = dates.pop() || "";
      if (!date) {
        throw new Error("No chart data available");
      }
      try {
        results = await getChartDataByCountry(chartCountryCode, date);
      } catch (e) {
        console.warn("Error fetching chart data", chartCountryCode, date, e);
      }
    } while (!results || results.length === 0);
    return { date, results };
  }

  async function getMostRecentChartByLanguage(languageCode: string) {
    const chartMonths = await getChartMonthsByLanguage(languageCode);
    const dates = sortBy(chartMonths, (c) => c);
    let results: FESearchResult[] | null = null;
    let date: string = "";
    do {
      date = dates.pop() || "";
      if (!date) {
        throw new Error("No chart data available");
      }
      try {
        results = await getChartDataByLanguage(languageCode, date);
      } catch (e) {
        console.warn("Error fetching chart data", languageCode, date, e);
      }
    } while (!results || results.length === 0);
    return { date, results };
  }

  async function getAvailableCountries() {
    const url = `${AppConstants.CHART_BASE_COUNTRY_URL}/countryConfig.json`;

    const data = await tokenFetch<ChartCountryInfo[]>(
      `${AppConstants.CHART_BASE_COUNTRY_URL}/countryConfig.json`,
      true,
    );

    availableCountries.value = data.filter((c) => c.Visible);
  }

  async function getAvailableLanguages() {
    const data = await tokenFetch<ChartLanguageInfo[]>(
      `${AppConstants.CHART_BASE_LANGUAGE_URL}/languageConfig.json`,
      true,
    );

    availableChartsByLanguage.value = data
      .filter((c) => c.visible)
      .map((c) => {
        const chartType =
          c.limitTo === "100"
            ? "ccli-top-100-by-language"
            : "ccli-top-songs-by-language";

        const url = `/${chartType}/${c.languageCode.toLowerCase()}`;
        return {
          chartType: "language",
          urlChartType: chartType,
          chartTypeLabel:
            chartType === "ccli-top-100-by-language"
              ? t("SV.LIT_CCLITop100")
              : t("G.LIT_CCLITopSongs"), // i.e. $('SV.LIT_CCLITop100')
          url: url,
          limitTo: parseInt(c.limitTo || ""),
          code: c.languageCode.toLowerCase(),
          codeLabel: c.languageName,
        };
      });
  }

  async function getChartDataByCountry(countryCode: string, weekOf: string) {
    if (
      cachedChartDataByCountry.value[countryCode] &&
      cachedChartDataByCountry.value[countryCode][weekOf]
    ) {
      return Array.from(cachedChartDataByCountry.value[countryCode][weekOf]);
    }

    if (!isCCLICountryChartAvailable(countryCode)) {
      console.error("Invalid country code", countryCode);
      throw new Error("Invalid country code");
    }

    const chartWeeks = await getChartWeeksByCountry(countryCode);

    if (chartWeeks.indexOf(weekOf) === -1) {
      throw new Error("Invalid week of date");
    }

    const results = await tokenFetch<CountryChartRowItem[]>(
      `${AppConstants.CHART_BASE_COUNTRY_URL}/${countryCode.toUpperCase()}/${weekOf}.json`,
      true,
    ).catch((err) => {
      throw err;
    });

    if (!results) {
      console.error("no results found for", countryCode, weekOf);
      throw new Error("No results found for this week");
    }

    const transformedResults: FESearchResult[] = await Promise.all(
      results.map(async (r) => {
        const badges: ChartBadge[] = [];
        if (r.IsPublicDomain) {
          badges.push("public-domain");
        }
        if (r.IsTrending) {
          badges.push("trending");
        }
        if (r.IsReEntry) {
          badges.push("re-entry");
        }
        if (r.IsNewEntry) {
          badges.push("new-entry");
        }
        if (r.IsEvergreen) {
          badges.push("evergreen");
        }

        const data: FESearchResult = {
          title: r.Title,
          authors: r.Authors.split("^"),
          slug: slugify(r.SongTitle),
          rank: r.ChartPosition,
          rankMovement: r.ChartMovement,
          periodsOnChart: r.WeeksInChart,
          songNumber: r.SongNumber.toString(),
          soundSample: {
            audioUrl: r.SoundSampleURL,
            imageUrl: r.AlbumArtURL,
          },
          productExists: {
            chords: r.HasChordSheet,
            lead: r.HasLeadSheet,
            lyrics: r.HasLyrics,
            multitracks: false,
            vocal: r.HasHymnSheet,
          },
          badges,
        };

        const songVersion = await songStore.getSongVersions(
          r.SongNumber.toString(),
        );
        if (songVersion) {
          data.title = songVersion.SongTitle;
          data.artist = songVersion.Artist;
          data.album = songVersion.Album;
          data.soundSample = {
            audioUrl: songVersion.SoundSampleURL,
            imageUrl: songVersion.AlbumArtURL,
          };
          data.enableSongVersion = true;
        } else {
          data.enableSongVersion = false;
        }

        return data;
      }),
    );

    if (!cachedChartDataByCountry.value[countryCode]) {
      cachedChartDataByCountry.value[countryCode] = {};
    }
    cachedChartDataByCountry.value[countryCode][weekOf] = transformedResults;

    return Array.from(cachedChartDataByCountry.value[countryCode][weekOf]);
  }

  async function getChartDataByLanguage(languageCode: string, monthOf: string) {
    if (
      cachedChartDataByLanguage.value[languageCode] &&
      cachedChartDataByLanguage.value[languageCode][monthOf]
    ) {
      return Array.from(cachedChartDataByLanguage.value[languageCode][monthOf]);
    }

    if (!isCCLILanguageChartAvailable(languageCode)) {
      console.error("Invalid language code", languageCode);
      throw new Error("Invalid language code");
    }

    const chartMonths = await getChartMonthsByLanguage(languageCode);

    if (chartMonths.indexOf(monthOf) === -1) {
      throw new Error("Invalid month of date");
    }

    const url = `${AppConstants.CHART_BASE_LANGUAGE_URL}/${languageCode}/${monthOf}.json`;

    const results = await tokenFetch<LanguageChartRowItem[]>(url, true).catch(
      (err) => {
        throw err;
      },
    );

    if (!results) {
      console.error("no results found for", languageCode, monthOf);
      throw new Error("No results found for this month");
    }

    const transformedResults: FESearchResult[] = await Promise.all(
      results.map(async (r) => {
        const badges: ChartBadge[] = [];
        if (r.IsPublicDomain) {
          badges.push("public-domain");
        }
        if (r.IsTrending) {
          badges.push("trending");
        }
        if (r.IsReEntry) {
          badges.push("re-entry");
        }
        if (r.IsNewEntry) {
          badges.push("new-entry");
        }
        if (r.IsEvergreen) {
          badges.push("evergreen");
        }

        const data: FESearchResult = {
          title: r.SongTitle,
          authors: r.Authors.split("^"),
          slug: slugify(r.SongTitle),
          rank: r.ChartPosition,
          rankMovement: r.ChartMovement,
          periodsOnChart: r.MonthsInChart,
          songNumber: r.SongNumber.toString(),
          soundSample: {
            audioUrl: r.SoundSampleURL,
            imageUrl: r.AlbumArtURL,
          },
          productExists: {
            chords: r.HasChordSheet,
            lead: r.HasLeadSheet,
            lyrics: r.HasLyrics,
            multitracks: false,
            vocal: r.HasHymnSheet,
          },
          badges,
        };

        const songVersion = await songStore.getSongVersions(
          r.SongNumber.toString(),
        );
        if (songVersion) {
          data.title = songVersion.SongTitle;
          data.artist = songVersion.Artist;
          data.album = songVersion.Album;
          data.soundSample = {
            audioUrl: songVersion.SoundSampleURL,
            imageUrl: songVersion.AlbumArtURL,
          };
          data.enableSongVersion = true;
        } else {
          data.enableSongVersion = false;
        }

        return data;
      }),
    );

    if (!cachedChartDataByLanguage.value[languageCode]) {
      cachedChartDataByLanguage.value[languageCode] = {};
    }
    cachedChartDataByLanguage.value[languageCode][monthOf] = transformedResults;

    return Array.from(cachedChartDataByLanguage.value[languageCode][monthOf]);
  }

  async function init() {
    const promises = [getAvailableCountries()];

    if (beta.features.ccliTop100ByLanguage) {
      promises.push(getAvailableLanguages());
    }
    await Promise.all(promises);
  }

  if (beta.features.ccliTop100New) {
    init();
  }

  return {
    defaultChart,
    init,
    getChartDataByCountry,
    getChartDataByLanguage,
    getChartWeeksByCountry,
    getChartMonthsByLanguage,
    getMostRecentChartByCountry,
    getMostRecentChartByLanguage,
    availableCountries,
    availableChartsByCountry,
    availableChartsByLanguage,
  };
});
